import * as yup from 'yup';

import { requiredString } from 'utils/constants';
import { maxLimit, PHONE_NUMBER_REGEX, ZIP_CODE_REGEX } from 'utils/validators';

import { ServiceTypeEnum } from 'utils/enums';
import { facilityFields, integrationMessageTypes } from './utils';


const facilityIntegrationConfigurations = yup.object({
  [integrationMessageTypes.RECEIVE_ORDERS]: yup.object({
    enabled: yup.boolean(),
    consultTypes: yup
      .array()
      .of(yup.string())
      .when('enabled', {
        is: true,
        then: (schema) =>
          schema.min(1, 'Consult Types are required for Receive Orders'),
        otherwise: (schema) => schema.notRequired(),
      }),
    target: yup.string().when('enabled', {
      is: true,
      then: (schema) =>
        schema.required('Target is required for Receive Orders'),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
  [integrationMessageTypes.SEND_SUMMARY]: yup.object({
    enabled: yup.boolean(),
    consultTypes: yup
      .array()
      .of(yup.string())
      .when('enabled', {
        is: true,
        then: (schema) =>
          schema.min(1, 'Consult Types are required for Send Summary'),
        otherwise: (schema) => schema.notRequired(),
      }),
  }),
  [integrationMessageTypes.SEND_BILLING]: yup.object({
    enabled: yup.boolean(),
    consultTypes: yup
      .array()
      .of(yup.string())
      .when('enabled', {
        is: true,
        then: (schema) =>
          schema.min(1, 'Consult Types are required for Send Billing'),
        otherwise: (schema) => schema.notRequired(),
      }),
    target: yup.string().when('enabled', {
      is: true,
      then: (schema) => schema.required('Target is required for Send Billing'),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
});

const facilityIntegrationCodeMapping = yup
  .object()
  .shape({
    orderId: yup.string().nullable().trim(),
    orderText: yup.string().nullable().trim(),
    services: yup
      .array()
      .of(yup.string().oneOf(Object.values(ServiceTypeEnum)))
      .nullable()
      .default([]),
  })
  .test(
    'at-least-one-field',
    'Each mapping row must have Order ID, Order Text, or at least one Service Line',
    (value) => {
      if (!value) return true;
      const hasOrderId = Boolean(value.orderId?.trim());
      const hasOrderText = Boolean(value.orderText?.trim());
      const hasServices =
        Array.isArray(value.services) && value.services.length > 0;
      return hasOrderId || hasOrderText || hasServices;
    },
  );
export default yup.object().shape({
  [facilityFields.active]: yup.bool(),
  [facilityFields.name]: yup
    .string()
    .trim()
    .required(requiredString)
    .max(50, maxLimit),
  [facilityFields.street]: yup.string().nullable().trim().max(180, maxLimit),
  [facilityFields.city]: yup.string().nullable().trim().max(100, maxLimit),
  [facilityFields.zipCode]: yup
    .string()
    .nullable()
    .trim()
    .max(5, maxLimit)
    .matches(ZIP_CODE_REGEX, 'Invalid zip code'),
  [facilityFields.phone]: yup
    .string()
    .nullable()
    .trim()
    .matches(PHONE_NUMBER_REGEX, 'Invalid phone number'),
  [facilityFields.itPhone]: yup
    .string()
    .nullable()
    .trim()
    .matches(PHONE_NUMBER_REGEX, 'Invalid phone number'),
  [facilityFields.callCenterPhone]: yup
    .string()
    .nullable()
    .trim()
    .matches(PHONE_NUMBER_REGEX, 'Invalid phone number'),
  [facilityFields.fax]: yup
    .string()
    .nullable()
    .trim()
    .matches(PHONE_NUMBER_REGEX, 'Invalid fax number')
    .when(facilityFields.autoFax, {
      is: true,
      then: (schema) =>
        schema.required(
          'Primary Fax Number is required when Auto-fax is enabled',
        ),
    }),
  [facilityFields.secondaryFaxNumber]: yup
    .string()
    .nullable()
    .trim()
    .matches(PHONE_NUMBER_REGEX, 'Invalid fax number'),
  [facilityFields.emrName]: yup.string().nullable().trim().max(180, maxLimit),
  [facilityFields.emrLink]: yup
    .string()
    .nullable()
    .trim()
    .max(180, maxLimit)
    .url('Invalid url'),
  [facilityFields.radiology]: yup.string().nullable().trim().max(180, maxLimit),
  [facilityFields.radiologyLink]: yup
    .string()
    .nullable()
    .trim()
    .max(180, maxLimit)
    .url('Invalid url'),
  [facilityFields.sharedSignout]: yup
    .string()
    .nullable()
    .trim()
    .max(180, maxLimit),
  [facilityFields.sharedSignoutLink]: yup
    .string()
    .nullable()
    .trim()
    .max(180, maxLimit)
    .url('Invalid url'),
  [facilityFields.camera]: yup.string().nullable().trim().max(180, maxLimit),
  [facilityFields.cameraLink]: yup
    .string()
    .nullable()
    .trim()
    .max(180, maxLimit)
    .url('Invalid url'),
  [facilityFields.facilityIntegrationConfigurations]: yup
    .mixed()
    .test(
      'optional-or-valid-config',
      'Invalid integration configuration',
      function validateIntegrationConfiguration(value) {
        const { path, createError } = this;
        if (!value || typeof value !== 'object') return true;
        const hasAnyEnabled = Object.values(value).some(
          (v) => v?.enabled === true,
        );
        if (!hasAnyEnabled) return true;

        const enabledKeys = Object.entries(value)
          .filter(([, config]) => config?.enabled)
          .map(([key]) => key);

        const dynamicShape = enabledKeys.reduce((acc, key) => {
          acc[key] = facilityIntegrationConfigurations.fields[key];
          return acc;
        }, {});

        const dynamicSchema = yup.object().shape(dynamicShape);

        try {
          dynamicSchema.validateSync(value, { abortEarly: false });
          return true;
        } catch (err) {
          return createError({
            path,
            message:
              err?.errors?.[0] ||
              'One or more enabled integrations are missing required fields',
          });
        }
      },
    ),
  facilityIntegrationCodeMappings: yup
    .array()
    .of(facilityIntegrationCodeMapping)
    .when(
      `${facilityFields.facilityIntegrationConfigurations}.${integrationMessageTypes.RECEIVE_ORDERS}.enabled`,
      {
        is: true,
        then: (schema) => schema.default([]),
        otherwise: (schema) => schema.nullable().notRequired(),
      },
    ),
});
